import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import type { QueueItem } from "../../api/types.js";
import {
  categoryLabel,
  formatDate,
  formatProviderShort,
  formatQuantityShort,
  formatTimingShort,
  missingFacts,
  needsAttentionMark,
  placeGroupLabel,
  placeLabel,
  queueReason,
  queueReasonShort,
  timingIsUnknown,
} from "../../app/presentation.js";

export type WorkbenchGrouping = "attention" | "place";

export interface WorkbenchRow {
  readonly item: QueueItem;
  /** Stable identity for selection and refresh: one row is one Service Profile. */
  readonly key: string;
}

export interface WorkbenchTableProps {
  readonly items: readonly QueueItem[];
  readonly grouping: WorkbenchGrouping;
  readonly selectedKey: string | null;
  readonly canWriteInteractions: boolean;
  readonly onSelect: (item: QueueItem) => void;
  readonly onOpenLab: (labId: string) => void;
  readonly onLogInteraction: (item: QueueItem) => void;
}

/**
 * One desktop row per Service Profile, so a Lab with pipettes and balances is
 * two rows and category meaning is never merged. Columns stay deliberately
 * small: only what makes or executes a sales decision.
 */
export function WorkbenchTable({
  items,
  grouping,
  selectedKey,
  canWriteInteractions,
  onSelect,
  onOpenLab,
  onLogInteraction,
}: WorkbenchTableProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [cursorKey, setCursorKey] = useState<string | null>(null);

  const groups = useMemo(() => groupRows(items, grouping), [items, grouping]);

  useEffect(() => {
    // A refreshed set can drop the row the keyboard cursor pointed at.
    if (
      cursorKey !== null &&
      !items.some((item) => item.serviceProfileId === cursorKey)
    ) {
      setCursorKey(null);
    }
  }, [cursorKey, items]);

  function moveCursor(from: string, delta: number) {
    const index = items.findIndex((item) => item.serviceProfileId === from);
    if (index === -1) return;
    const next = items[index + delta];
    if (next === undefined) return;
    setCursorKey(next.serviceProfileId);
    const row = bodyRef.current?.querySelector<HTMLElement>(
      `[data-row-key="${next.serviceProfileId}"]`,
    );
    row?.focus();
  }

  return (
    <div className="workbench-scroll">
      <table className="workbench-table">
        <caption className="sr-only">
          Working set of Labs and service categories. Use the arrow keys to move
          between rows and Enter to open a Lab.
        </caption>
        <colgroup>
          <col className="col-lab" />
          <col className="col-place" />
          <col className="col-number" />
          <col className="col-timing" />
          <col className="col-provider" />
          <col className="col-contact" />
          <col className="col-why" />
          <col className="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Lab</th>
            <th scope="col">Place</th>
            <th scope="col">Qty</th>
            <th scope="col">Timing</th>
            <th scope="col">Provider</th>
            <th scope="col">Contact</th>
            <th scope="col">Next attention</th>
            <th scope="col">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {groups.map((group) => (
            <GroupRows
              key={group.key}
              group={group}
              grouping={grouping}
              selectedKey={selectedKey}
              cursorKey={cursorKey}
              canWriteInteractions={canWriteInteractions}
              onSelect={onSelect}
              onOpenLab={onOpenLab}
              onLogInteraction={onLogInteraction}
              onKeyDown={(event, key) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  moveCursor(key, 1);
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  moveCursor(key, -1);
                } else if (event.key === "Enter") {
                  const item = items.find(
                    (entry) => entry.serviceProfileId === key,
                  );
                  if (item) onOpenLab(item.lab.id);
                } else if (event.key === " ") {
                  event.preventDefault();
                  const item = items.find(
                    (entry) => entry.serviceProfileId === key,
                  );
                  if (item) onSelect(item);
                }
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({
  group,
  grouping,
  selectedKey,
  cursorKey,
  canWriteInteractions,
  onSelect,
  onOpenLab,
  onLogInteraction,
  onKeyDown,
}: {
  group: WorkbenchGroup;
  grouping: WorkbenchGrouping;
  selectedKey: string | null;
  cursorKey: string | null;
  canWriteInteractions: boolean;
  onSelect: (item: QueueItem) => void;
  onOpenLab: (labId: string) => void;
  onLogInteraction: (item: QueueItem) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>, key: string) => void;
}) {
  return (
    <>
      <tr className="workbench-group">
        <th scope="colgroup" colSpan={8}>
          <span className="workbench-group-label">{group.label}</span>
          <span className="workbench-group-count">
            {group.items.length}{" "}
            {grouping === "place" ? "service contexts" : "rows"}
          </span>
        </th>
      </tr>
      {group.items.map((item) => {
        const selected = selectedKey === item.serviceProfileId;
        return (
          <tr
            key={item.serviceProfileId}
            data-row-key={item.serviceProfileId}
            data-selected={selected || undefined}
            data-attention={needsAttentionMark(item) || undefined}
            className={
              cursorKey === item.serviceProfileId
                ? "workbench-row cursor"
                : "workbench-row"
            }
            tabIndex={-1}
            aria-selected={selected}
            onFocus={() => onSelect(item)}
            onKeyDown={(event) => onKeyDown(event, item.serviceProfileId)}
          >
            <th scope="row" className="cell-lab">
              <button
                type="button"
                className="row-open"
                onClick={() => onOpenLab(item.lab.id)}
              >
                {item.lab.name}
                <span className="row-category">
                  {categoryLabel(item.serviceCategory)}
                </span>
              </button>
            </th>
            <td className="cell-place" data-tip={placeGroupLabel(item)}>
              {placeLabel(item)}
            </td>
            <td className="cell-number">
              {item.projection.currentQuantityValue === null ? (
                <span className="cell-quiet">—</span>
              ) : (
                formatQuantityShort(item.projection)
              )}
            </td>
            <td
              className={
                timingIsUnknown(item.projection) ? "cell-quiet" : undefined
              }
            >
              {timingIsUnknown(item.projection)
                ? "Unknown"
                : formatTimingShort(item.projection)}
            </td>
            <td
              className={
                item.projection.currentProviderName ? undefined : "cell-quiet"
              }
            >
              {item.projection.currentProviderName?.trim()
                ? formatProviderShort(item.projection)
                : "—"}
            </td>
            <td>
              {item.contact === null ? (
                <span className="cell-quiet">—</span>
              ) : (
                <span className="cell-contact">
                  <span className="row-name">{item.contact.name}</span>
                  {item.contact.verificationStatus === "unverified" && (
                    <span className="cell-sub">unverified</span>
                  )}
                </span>
              )}
            </td>
            <td className="cell-why" data-tip={whyDetail(item)}>
              <span className="cell-next">
                {formatDate(item.queue.nextMeaningfulSalesDate)}
              </span>
              <span className="cell-why-reason">
                {queueReasonShort(item.queue)}
              </span>
            </td>
            <td className="cell-actions">
              {canWriteInteractions && (
                <button
                  type="button"
                  className="row-action"
                  aria-label={`Record interaction at ${item.lab.name}`}
                  onClick={() => onLogInteraction(item)}
                >
                  Record
                </button>
              )}
              <button
                type="button"
                className="row-action"
                aria-label={`Open ${item.lab.name}`}
                onClick={() => onOpenLab(item.lab.id)}
              >
                Open
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

interface WorkbenchGroup {
  readonly key: string;
  readonly label: string;
  readonly items: readonly QueueItem[];
}

/**
 * Grouping is presentation only: it never changes which rows the server
 * returned, and it never hides urgency. Ordering inside a group stays the
 * server's attention order so the most urgent row is first in its group.
 */
export function groupRows(
  items: readonly QueueItem[],
  grouping: WorkbenchGrouping,
): readonly WorkbenchGroup[] {
  const labelFor =
    grouping === "place"
      ? placeGroupLabel
      : (item: QueueItem) => attentionBucket(item);
  const collected = new Map<string, QueueItem[]>();
  for (const item of items) {
    const label = labelFor(item);
    const bucket = collected.get(label);
    if (bucket === undefined) {
      collected.set(label, [item]);
    } else {
      bucket.push(item);
    }
  }
  return [...collected.entries()].map(([label, groupItems]) => ({
    key: label,
    label,
    items: groupItems,
  }));
}

/**
 * Attention buckets follow the queue code the server calculated, so grouping
 * cannot contradict queue membership. Unknown timing is its own bucket and is
 * deliberately not framed as work.
 */
export function attentionBucket(item: QueueItem): string {
  if (item.queue.queueCode === "NOW") return "Needs attention";
  if (item.queue.queueCode === "DISQUALIFIED_SERVICE_NEED") {
    return "Review service fit";
  }
  if (timingIsUnknown(item.projection)) return "Timing unknown";
  if (item.queue.queueCode === "COMING") return "Coming up";
  return "Later";
}

/**
 * The full sentence behind a row's short reason, plus what is still missing.
 * It rides a hover tooltip so the ledger row stays one line of ink while the
 * complete explanation remains one gesture away.
 */
export function whyDetail(item: QueueItem): string {
  const reason = queueReason(item.queue);
  const missing = missingFacts(item);
  return missing ? `${reason} Still needed: ${missing}.` : reason;
}
